class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

/**
 * The makeup of a section to be used by our SectionRenderer
 *
 * @typedef {Object} Section
 * @property {string} id - The name of the section. This will be the filename of the section
 * @property {string} section - The ID of the section. This can be uniquely generated by Shopify
 *   for a repeatable section
 * @property {string} selector - The CSS selector which contains the sections content
 * @property {string} [html] - This is the HTML returned by the section rendering API
 * @property {Function} [convertor] - This will be ran on the new HTML before it's inserted into the
 * @property {boolean} [dontRender] - Sometimes we want to get a section back but no render it, like
 *   a cart count DOM
 */
window.BAO.sectionRenderer = new (class SectionRenderer {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * Request a section, or sections, from the section rendering API
   *
   * @param {string|string[]} sections - The names of the sections we want to fetch
   * @param {string} [sectionsUrl] - The context in which we want the sections to be
   *   rendered in
   *
   * @returns {Promise<Object>}
   */
  async fetch (sections, sectionsUrl = this.sectionsUrl) {
    if (typeof sections === 'string') {
      sections = [sections]
    }

    const [path, querystring = ''] = sectionsUrl.split('?')

    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(querystring)
    params.append('sections', sections.join(','))

    return this.#fetch(`${path}?${params.toString()}`)
  }

  /**
   * Request a single section from the section rendering API
   * https://shopify.dev/api/section-rendering#request-a-single-section
   *
   * @param {string} section - The names of the section we want to fetch
   * @param {string} [sectionsUrl] - The context in which we want the sections to be
   *   rendered in
   *
   * @returns {Promise<string>}
   */
  async fetchSingle (section, sectionsUrl = window.location.pathname) {
    const [path, querystring = ''] = sectionsUrl.split('?')

    // eslint-disable-next-line compat/compat
    const params = new URLSearchParams(querystring)
    params.set('section_id', section)

    return this.#fetch(`${path}?${params.toString()}`)
  }

  /**
   * Render a section returned from the section rendering API
   *
   * @param {Section} section - The makeup of the section we want to render
   *
   * @returns {void}
   */
  render (section) {
    if ('dontRender' in section && section.dontRender) return

    this.validate(section)

    const elementToReplace =
      document.getElementById(section.id)?.querySelector(section.selector) ||
      document.getElementById(section.id)

    if (!elementToReplace) {
      console.info(
        `Can't find ${section.id} even though we are trying to render it`,
      )

      return
    }

    const newHTML = this.getOuterHtml(section.html, {
      id: section.id,
      selector: section.selector,
    })
    const oldHTML = elementToReplace.outerHTML

    elementToReplace.outerHTML =
      typeof section.convertor === 'function'
        ? section.convertor(oldHTML, newHTML)
        : newHTML
  }

  /**
   * Render multiple sections returned from the section rendering API
   *
   * @param {Section[]} sections - The sections we want to render
   *
   * @returns {void}
   */
  renderMultiple (sections) {
    sections.forEach(section => this.render(section))
  }

  /**
   * Grab the HTML that is the section
   *
   * @param {string} html - The section HTML returned from the section rendering API
   * @param {{ id: string, selector: string }} selector
   *
   * @returns {string}
   */
  getOuterHtml (html, { id = '', selector = '.shopify-section' } = {}) {
    const dom = this.convertToDOM(html)
    const element =
      dom.getElementById(id)?.querySelector(selector) || dom.getElementById(id)

    return element?.outerHTML
  }

  /**
   * Grab the HTML that is inside the section otherwise we end up with section inception
   *
   * @param {string} html - The section HTML returned from the section rendering API
   * @param {{ id: string, selector: string }} selector
   *
   * @returns {string}
   */
  getInnerHtml (html, { id = '', selector = '.shopify-section' } = {}) {
    const dom = this.convertToDOM(html)
    const element =
      dom.getElementById(id)?.querySelector(selector) || dom.getElementById(id)

    return element?.innerHTML
  }

  /**
   * Convert a HTML string into a parseable DOM
   *
   * @param {string} html
   *
   * @returns {Document}
   */
  convertToDOM (html) {
    return new DOMParser().parseFromString(html, 'text/html')
  }

  /**
   * Render a section returned from the section rendering API
   *
   * @param {Section} section - The makeup of the section we want to render
   *
   * @returns {boolean}
   */
  validate (section) {
    const isValid = this.expectedKeys.every(key => key in section)

    if (!isValid) {
      throw new Error(
        `section is missing these keys: "${this.expectedKeys
          .filter(k => !(k in section))
          .join(', ')}"`,
      )
    }

    return isValid
  }

  async #fetch (pathName) {
    const anchor = document.createElement('a')
    anchor.href = pathName

    const url = new URL(
      `${anchor.protocol}//${anchor.host}${anchor.pathname}${anchor.search}`,
    )
    const section = url.searchParams.get('section_id')
    const sections = url.searchParams.get('sections')

    if (
      section &&
      this.cache.exists(this.getCacheKey(section, url.toString()))
    ) {
      return this.cache.get(this.getCacheKey(section, url.toString()))
    } else if (
      sections &&
      this.cache.exists(this.getCacheKey(sections, url.toString()))
    ) {
      const result = this.cache.get(this.getCacheKey(section, url.toString()))

      return JSON.parse(result)
    }

    const response = await fetch(url.toString())

    if (section) {
      const html = await response.text()

      this.cache.set(this.getCacheKey(section, url.toString()), html)

      return html
    } else if (sections) {
      const json = await response.json()

      this.cache.set(
        this.getCacheKey(sections, url.toString()),
        JSON.stringify(json),
      )

      return json
    }

    throw new Error('No section or sections found in url')
  }

  getCacheKey (section, url) {
    return `${section}|${url}`
  }

  get expectedKeys () {
    return ['id', 'selector', 'html']
  }

  /**
   * @returns {Section[]}
   */
  get pushCartSections () {
    return [
      {
        id: 'push-cart-shipping',
        section: 'push-cart-shipping',
        selector: '.shopify-section',
      },
      {
        id: 'push-cart-items',
        section: 'push-cart-items',
        selector: '.shopify-section',
      },
      {
        id: 'push-cart-recommendations',
        section: 'push-cart-recommendations',
        selector: '.shopify-section',
      },
      {
        id: 'push-cart-footer',
        section: 'push-cart-footer',
        selector: '.shopify-section',
      },
      {
        id: 'push-cart-count',
        section: 'push-cart-count',
        selector: '.shopify-section',
        dontRender: true,
      },
    ]
  }

  /**
   * @returns {Section[]}
   */
  get cartSections () {
    return [
      {
        id: 'template-cart-items',
        section: document.getElementById('template-cart-items').dataset.id,
        selector: '.js-Contents',
      },
    ]
  }

  /**
   * @returns {string} The current url pathname and query parameters
   */
  get sectionsUrl () {
    return `${window.location.pathname}${window.location.search}`
  }
})()
